import React, { useEffect, useState, useRef } from 'react';
import { LeftOutlined, PlusOutlined, ImportOutlined } from '@ant-design/icons';
import { addField, removeField } from '@/store/reducers/headerContent';
import { Input, Tooltip, Form, Button } from 'antd';
import { toast } from 'react-toastify';
import { import_recipe_json } from '../../utils/api';

import { useLocation } from 'react-router-dom';
import DAGFlowCopy from '@/pages/dashboard/ArchiMateFlow';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';

// New imports for refactored architecture
import { useProductData } from '@/hooks/recipe';

function RecipeConfig({
  selectedButton,
  recipe_id,
  selectedRecipeName,
  setSelectedRecipeName,
  product_id,
  searchvalue,
  setSearchvalue,
  grid_list,
  set_product_id,
  set_recipe_id,
  set_grid_list,
  pendingStatus,
}) {
  const { isDark } = useTheme();
  const location = useLocation();
  const accessData = accessControlUtils('View MBMR') || {
    create_access: false,
    read_access: false,
    update_access: false,
    delete_access: false,
  };
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedModule = useSelector((state) => state.moduleView);

  // Use Product Data hook instead of manual API calls
  const { products: product_list } = useProductData(selectedButton, selectedModule, {
    autoFetch: true,
    enableSearch: true,
  });

  // Local state - only what's actually used
  const [flow_data, set_flow_data] = useState();
  const [importLoading, setImportLoading] = useState(false);
  const importFileRef = useRef(null);

  const handleImportRecipe = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const jsonData = JSON.parse(evt.target.result);
        await import_recipe_json(
          { recipe_data: jsonData, product_id: product_id },
          (res) => {
            if (res?.data?.status === 'success') {
              toast.success(`Recipe "${res.data.data?.new_recipe_name}" imported successfully`);
              window.location.reload();
            } else {
              toast.error(res?.data?.message || 'Import failed');
            }
            setImportLoading(false);
          },
          (err) => {
            toast.error(err?.response?.data?.message || 'Import failed');
            setImportLoading(false);
          }
        );
      } catch (parseErr) {
        toast.error('Invalid JSON file');
        setImportLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleClick = (button) => {
    if (button !== 'list' && button !== 'grid') {
      set_flow_data();
    } else {
      set_grid_list(button);
    }
  };

  const clearSearchValue = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setSearchvalue();
  };

  // Set flow data when product/recipe changes
  useEffect(() => {
    if (!product_id) {
      set_flow_data();
    } else if (product_id || recipe_id) {
      set_flow_data();
      setTimeout(() => {
        set_flow_data({ recipe_id: recipe_id });
      }, 0.5);
    }
    clearSearchValue();
  }, [product_id, recipe_id]);

  useEffect(() => {
    // set product name and recipe name to show in header
    if ((product_list && product_list?.length > 0) || selectedRecipeName) {
      const productName =
        product_list?.find((p) => p.product_id === product_id)?.product_name ||
        'No Product Selected';

      productName && dispatch(addField({ key: 'Product', value: productName || '' }));
      selectedRecipeName && dispatch(addField({ key: 'MBMR', value: selectedRecipeName || '' }));
    }
  }, [product_id, recipe_id, selectedRecipeName]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Form.Item
            label={
              <span className="font-bold" style={{ color: isDark ? '#ffffff' : '#204448' }}>
                Product
              </span>
            }
            className="mb-0"
          >
            <div
              className="px-3 py-1.5 rounded text-sm min-w-[180px]"
              style={{
                backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f3f4f6',
                color: isDark ? '#ffffff' : '#204448',
              }}
            >
              {product_list?.find((p) => p.product_id === product_id)?.product_name ||
                'No Product Selected'}
            </div>
          </Form.Item>

          {selectedRecipeName && (
            <Form.Item
              label={
                <span className="font-bold" style={{ color: isDark ? '#ffffff' : '#204448' }}>
                  {selectedModule === 'prod' ? 'MBMR' : 'MBPR'}
                </span>
              }
              className="mb-0"
            >
              <div
                className="px-3 py-1.5 rounded text-sm min-w-[180px]"
                style={{
                  backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f3f4f6',
                  color: isDark ? '#ffffff' : '#204448',
                }}
              >
                {selectedRecipeName || 'No Recipe Selected'}
              </div>
            </Form.Item>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {product_id && !recipe_id && (
            <>
              {selectedButton === 'test' &&
                (accessData.create_access || accessData.update_access) && (
                  <>
                    <Tooltip title={`Import ${selectedModule === 'prod' ? 'MBMR' : 'MBPR'}`} placement="left">
                      <Button
                        icon={<ImportOutlined />}
                        loading={importLoading}
                        style={{
                          backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                          borderColor: isDark ? '#0F172A' : defaultColors.primary,
                          color: '#fff',
                        }}
                        onClick={() => importFileRef.current?.click()}
                      >
                        Import {selectedModule === 'prod' ? 'MBMR' : 'MBPR'}
                      </Button>
                    </Tooltip>
                    <input
                      type="file"
                      ref={importFileRef}
                      accept=".json"
                      style={{ display: 'none' }}
                      onChange={handleImportRecipe}
                    />
                    <Tooltip
                      title={`Add ${selectedModule === 'prod' ? 'MBMR' : 'MBPR'}`}
                      placement="left"
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{
                          backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                          borderColor: isDark ? '#0F172A' : defaultColors.primary,
                        }}
                        onClick={() => {
                          navigate('/recipeMetadata', {
                            state: { product_id: product_id },
                          });
                        }}
                      >
                        Add
                      </Button>
                    </Tooltip>
                  </>
                )}
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  onClick={() => handleClick('list')}
                  className={`px-4 py-1.5 text-sm font-medium border
                  ${
                    grid_list === 'list'
                      ? 'text-white border-opacity-50'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                  }
                  rounded-l-md hover:opacity-90 transition`}
                  style={{
                    backgroundColor: grid_list === 'list' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                    borderColor: grid_list === 'list' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                  }}
                >
                  List
                </button>
                <button
                  onClick={() => handleClick('grid')}
                  className={`px-3 py-1.5 text-sm font-medium border
                      ${
                        grid_list === 'grid'
                          ? 'text-white border-opacity-50'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                      }
                      rounded-r-md hover:opacity-90 transition`}
                  style={{
                    backgroundColor: grid_list === 'grid' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                    borderColor: grid_list === 'grid' ? (isDark ? '#0F172A' : defaultColors.primary) : undefined,
                  }}
                >
                  Grid
                </button>
              </div>
            </>
          )}

          <Input.Search
            placeholder="Search here..."
            size="middle"
            ref={inputRef}
            allowClear
            value={searchvalue}
            className={product_id ? 'w-48' : 'w-full'}
            onSearch={(value) => {
              setSearchvalue(value);
            }}
            onChange={(e) => {
              setSearchvalue(e.target.value);
            }}
          />

          {product_id && (
            <Tooltip title="Back" placement="bottom">
              <Button
                type="primary"
                icon={<LeftOutlined />}
                style={{
                  backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                  borderColor: isDark ? '#0F172A' : defaultColors.primary,
                }}
                onClick={() => {
                  if (recipe_id) {
                    set_recipe_id();
                    setSelectedRecipeName('');
                    dispatch(removeField('MBMR', ''));
                  } else {
                    set_product_id();
                    setSelectedRecipeName('');
                    dispatch(removeField('Product', ''));
                    set_grid_list('grid');
                  }
                  clearSearchValue();
                }}
              >
                Back
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {flow_data?.recipe_id && (
        <DAGFlowCopy
          data={flow_data}
          set_flow_data={set_flow_data}
          isClickable={pendingStatus[recipe_id] && accessData.update_access ? true : false}
        />
      )}
    </div>
  );
}

export default RecipeConfig;
